import { mapGetters } from 'vuex'
import utilService from '../../../common/services/utilService'
import {
  isGenuineComplaint,
  isComplaintBreach as isComplaintBreachPure,
  toTimestamp,
  getCleanerClockDotStatus,
  deferredComplaints as deferredComplaintsPure,
  formatDurationMs,
} from './cleanerActivityUtils'

/**
 * Shared display + SLA helpers for the Cleaner Activity timeline.
 * Consuming components inherit the `companyGmt`, `slaMinutes`, and `shiftStart` props.
 */
export default {
  props: {
    companyGmt: { type: [Number, String], default: null },
    slaMinutes: { type: [Number, String], default: null },
    shiftStart: { type: Date, default: null },
    referenceTimeMs: { type: Number, default: null },
  },
  computed: {
    ...mapGetters(['roomIdMap']),
  },
  methods: {
    companyGmtValue() {
      return this.companyGmt != null && this.companyGmt !== '' ? this.companyGmt : null
    },
    toTimestamp,
    effectiveNowMs() {
      return Number.isFinite(this.referenceTimeMs) ? this.referenceTimeMs : Date.now()
    },
    formatTime(date) {
      return utilService.formatTimeInCompanyTz(date, this.companyGmtValue(), '--:--')
    },
    formatHM(date) {
      return utilService.formatHMInCompanyTz(date, this.companyGmtValue(), '')
    },
    complaintDurationMs(complaint) {
      if (!complaint || !complaint.openTime) return null
      const open = this.toTimestamp(complaint.openTime)
      if (Number.isNaN(open)) return null
      if (complaint.closeTime) {
        const responseTime = Number(complaint.responseTime)
        if (Number.isFinite(responseTime) && responseTime >= 0) return responseTime
        const close = this.toTimestamp(complaint.closeTime)
        return Number.isNaN(close) ? null : close - open
      }
      return this.effectiveNowMs() - open
    },
    isComplaintBreach(complaint) {
      return isComplaintBreachPure(complaint, this.slaMinutes, this.effectiveNowMs())
    },
    shouldShowComplaintDuration(complaint) {
      return !!complaint && !!complaint.closeTime && isGenuineComplaint(complaint)
    },
    getServikIcon(servikInfo) {
      if (!servikInfo) return null
      const { name, ctg, id, url } = servikInfo
      try {
        if (url) return url
        if (name && ctg != null && id != null) {
          return require(`@/assets/survey/servik_icons_white/${ctg}/${id}.${name}.svg`)
        }
      } catch (err) {
        return null
      }
      return null
    },
    getServikIconStyle(servikInfo) {
      if (!servikInfo) return {}
      const backgroundColor = servikInfo.backgroundColor || '#5f9b46'
      const color = Array.isArray(backgroundColor) ? backgroundColor[0] : backgroundColor
      return { backgroundColor: color, borderColor: color }
    },
    getServikLabel(servikInfo) {
      if (!servikInfo?.name) return 'complaint'
      return servikInfo.name.toLowerCase().split(' ').join('_')
    },
    buildServikIcons(complaint) {
      if (!complaint) return []
      const icons = []

      const pushIcon = (servikInfo, key) => {
        if (!servikInfo) return
        const servikId = servikInfo.id != null ? Number(servikInfo.id) : null
        icons.push({
          key,
          servikId: Number.isFinite(servikId) ? servikId : null,
          servikInfo,
          servikLabel: this.getServikLabel(servikInfo),
          servikIconUrl: this.getServikIcon(servikInfo),
          servikIconStyle: this.getServikIconStyle(servikInfo),
        })
      }

      pushIcon(complaint.servikInfo, 'main')

      const additionals = Array.isArray(complaint.additionals) ? [...complaint.additionals] : []
      additionals
        .filter((additional) => additional && additional.servikInfo)
        .sort((a, b) => this.toTimestamp(a.time) - this.toTimestamp(b.time))
        .forEach((additional, index) => {
          pushIcon(additional.servikInfo, `add-${index}-${additional.time || index}`)
        })

      return icons
    },
    formatDuration: formatDurationMs,
    roomName(roomId) {
      const room = this.roomIdMap && this.roomIdMap[roomId]
      return room && room.name ? room.name : `#${roomId}`
    },
    deferredComplaints(cleaner, nowMs = Date.now()) {
      return deferredComplaintsPure(cleaner, (id) => this.roomName(id), nowMs)
    },
    getCleanerClockDotStatus(cleaner) {
      return getCleanerClockDotStatus(cleaner, this.shiftStart)
    },
    clockDotTitle(status) {
      if (status === 'late') return this.$t('clocked_in_late')
      if (status === 'in') return this.$t('still_clocked_in')
      return this.$t('clocked_out')
    },
    buildCleanerClockHistory(cleaner) {
      const sessions = (cleaner.sessions || []).filter((session) => session.clockIn)
      if (!sessions.length) return []

      return [...sessions]
        .sort((a, b) => this.toTimestamp(a.clockIn) - this.toTimestamp(b.clockIn))
        .map((session, index) => {
          const isOpen = !session.clockOut
          const inLabel = this.formatHM(session.clockIn)
          const outLabel = session.clockOut ? this.formatHM(session.clockOut) : null
          const title = isOpen
            ? `${this.formatTime(session.clockIn)} → ${this.$t('still_clocked_in')}`
            : `${this.formatTime(session.clockIn)} → ${this.formatTime(session.clockOut)}`

          return {
            key: `session-${index}-${session.clockIn}`,
            inLabel,
            outLabel,
            isOpen,
            title,
          }
        })
    },
  },
}
